import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  Grid,
  Button,
  Select,
  Divider,
  TextField,
  InputLabel,
  Typography,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormHelperText,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { CheckBoxOutlineBlank, CheckBox } from "@material-ui/icons";

import toJs from "../../../hoc/toJS";
import select from "../../../utils/select";
import ROUTER from "../../../constants/router";

import MultiFileManager from "../../File/MultiFileManager/MultiFileManager";
import DuplicateCheckModal from "../../../components/Achievement/DuplicateCheckModal";

import { uploadMultiFile } from "../../../reducer/file/action";
import { updateAchievement, checkDuplicateAchievement } from "../../../reducer/achievement/action";
import { statisticUnaccepted } from "../../../reducer/profile/action";
import {
  getEmploymentContractById,
  getAllEmploymentContractByProfileId,
} from "../../../reducer/employmentContract/action";
import { notification } from "../../../reducer/home/action";

import EmploymentContractTable from "../../../components/EmployContractModal/EmploymentContractTable";
import EmploymentContractDetailTable from "../../../components/EmployContractModal/EmploymentContractDetailTable";
import { ACHIEVEMENT } from "../../../constants/selectOptions";

function AchievementUpdateModal(props) {
  const { updating, setUpdating } = props;
  const [disabled, setDisabled] = useState(true);

  const [values, setValues] = useState(null);
  const [profileList, setProfileList] = useState([]);
  const [detail, setDetail] = useState(null);
  const [fileUrls, setFileUrls] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [fileManagerKey, setFileManagerKey] = useState(0);
  const [helperText, setHelperText] = useState(false);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [duplicateList, setDuplicateList] = useState([]);
  const [pendingSubmit, setPendingSubmit] = useState(false);

  const profileListFromProps = props.profileList;
  const contractList = [];
  props?.employmentContractListById.forEach((e) => contractList.push(e.contract));

  useEffect(() => {
    if (updating) resetValues();
  }, [updating]);

  useEffect(() => {
    if (updating?._fromDuplicate) {
      setDisabled(false);
    }
  }, [updating]);

  useEffect(() => {
    if (updating?._fromDuplicate && props.profile?.id) {
      const isAlreadyMember = profileList.some((member) => member.profileId === props.profile.id);
      if (!isAlreadyMember) {
        setProfileList([
          ...profileList,
          {
            profileId: props.profile.id,
            roleInAchievement: "member",
            isInternal: true,
            externalName: "",
            timeAmount: null,
          },
        ]);
      }
    }
  }, [updating, props.profile]);

  useEffect(() => {
    const contract = props?.employmentContractListById?.find(
      (e) => e?.contract?.id === updating?.contractIdForKpi,
    )?.contract;
    setDetail(contract?.listKpi?.find((e) => e.id === updating.kpiIdForKpi));
  }, [props?.employmentContractListById, updating?.contractIdForKpi]);

  const resetValues = () => {
    setValues({
      ...updating,
    });
    setProfileList([...updating.achievementProfile] || []);
    setFileUrls(updating.fileUrl?.split("||").filter(Boolean) || []);
    setNewFiles([]);
    setFileManagerKey((prev) => prev + 1);
  };

  const handleChangeForm = (name, value) => {
    setValues({ ...values, [name]: value });
  };

  const handleChangeProfileList = (index, name, value) => {
    const temp = profileList;
    const tempElm = profileList[index];
    temp.splice(index, 1, { ...tempElm, [name]: value });
    setProfileList([...temp]);
  };

  const handleInsert = () => {
    const temp = profileList;
    temp.push({
      profileId: null,
      roleInAchievement: "member",
      isInternal: true,
      externalName: "",
      timeAmount: null,
    });
    setProfileList([...temp]);
  };

  const handleRemove = (index) => {
    if (profileList[index].profileId === values?.profileIdForKpi) handleChangeForm("profileIdForKpi", null);
    const temp = profileList;
    temp.splice(index, 1);
    setProfileList([...temp]);
  };

  const handleClose = () => {
    setDuplicateModalOpen(false);
    setDuplicateList([]);
    setPendingSubmit(false);
    if (!disabled) {
      resetValues();
      setDisabled(true);
    } else {
      setUpdating(null);
      setDisabled(true);
      setFileUrls([]);
      setNewFiles([]);
      setFileManagerKey((prev) => prev + 1);
    }
  };

  const updateAchievementItem = async (id, payload) => {
    let result = await props.updateAchievement(id, {
      ...payload,
      achievementProfile: profileList,
    });
    return result;
  };

  const checkForDuplicates = async (title, currentId) => {
    if (!title || title.trim() === "") return false;

    const result = await props.checkDuplicateAchievement({
      publicationType: "achievements",
      title: title.trim(),
    });

    if (result.success && result.data && result.data.length > 0) {
      const filteredList = result.data.filter((item) => item.achievement?.id !== currentId);
      if (filteredList.length > 0) {
        setDuplicateList(filteredList);
        setDuplicateModalOpen(true);
        return true;
      }
    }
    return false;
  };

  const handleSelectDuplicate = (selectedAchievement) => {
    setDuplicateModalOpen(false);
    setUpdating({ ...selectedAchievement, _fromDuplicate: true });
  };

  const handleCloseDuplicateModal = () => {
    setDuplicateModalOpen(false);
    setPendingSubmit(true);
  };

  const proceedWithSubmit = async () => {
    let finalFileUrl = fileUrls.join("||");
    if (newFiles.length > 0) {
      const uploadResult = await props.uploadMultiFile(values.id, newFiles, "achievement");
      if (uploadResult.success) finalFileUrl = [...fileUrls, ...uploadResult.data].join("||");
    }
    updateAchievementItem(values.id, { ...values, fileUrl: finalFileUrl }).then((success) => {
      if (success) {
        props.statisticUnaccepted({
          departmentId: 0,
          profileId: props.profile?.id,
        });
        if (props.role === "sudo-admin") props.statisticUnaccepted({ departmentId: 0, profileId: 0 });
      }
    });

    setUpdating(null);
    setDisabled(true);
    setHelperText(false);
    setPendingSubmit(false);
  };

  React.useEffect(() => {
    if (pendingSubmit) {
      proceedWithSubmit();
    }
  }, [pendingSubmit]);

  const handleSubmit = async () => {
    // Check required fields
    if (!values?.awardType || !values?.awardedProjectName || !values?.numOfAuthors) {
      setHelperText(true);
      return;
    }

    // Validate profileList for external members
    const hasEmptyExternalName = profileList.some((e) => !e.isInternal && e.externalName === "");
    if (hasEmptyExternalName) {
      setHelperText(true);
      return;
    }

    // Validate at least one internal member
    const hasInternalMember = profileList.some((e) => e.isInternal === true);
    if (!hasInternalMember) {
      props.pushNoti({
        type: "warning",
        message: "Phải có ít nhất 1 thành viên nội bộ của trường trong công trình khoa học!",
      });
      return;
    }

    // Validate purposeId = 1 requires profileIdForKpi
    if (values?.purposeId === 1 && !values?.profileIdForKpi) {
      setHelperText(true);
      return;
    }

    // Check for duplicates only when NOT in disabled mode (i.e., when editing)
    if (!disabled) {
      const hasDuplicates = await checkForDuplicates(values?.awardedProjectName, values?.id);
      if (hasDuplicates) {
        return;
      }
    }

    let finalFileUrl = fileUrls.join("||");
    if (newFiles.length > 0) {
      const uploadResult = await props.uploadMultiFile(values.id, newFiles, "achievement");
      if (uploadResult.success) finalFileUrl = [...fileUrls, ...uploadResult.data].join("||");
    }
    updateAchievementItem(values.id, { ...values, fileUrl: finalFileUrl }).then((success) => {
      if (success) {
        props.statisticUnaccepted({
          departmentId: 0,
          profileId: props.profile?.id,
        });
        if (props.role === "sudo-admin") props.statisticUnaccepted({ departmentId: 0, profileId: 0 });
      }
    });

    setUpdating(null);
    setValues(null);
    setHelperText(false);
  };

  const handleSendFeedback = () => {
    props.history.push({
      pathname: ROUTER.NOTIFICATION,
      state: {
        sendTo: "personal",
        title: "Phản hồi về việc khai báo thông tin Giải thưởng",
        chosenProfiles: profileList.reduce((accumulator, currentValue) => {
          accumulator.push(currentValue.profileId);
          return accumulator;
        }, []),
        data: updating,
        dataType: "achievement",
      },
    });
  };

  return (
    <Dialog fullWidth keepMounted maxWidth="lg" open={updating !== null} onClose={handleClose} disableBackdropClick>
      <DialogTitle>Chi tiết giải thưởng</DialogTitle>
      <DialogContent style={{ display: "flex", gap: 16, overflowX: "hidden" }}>
        <div style={{ flex: "1 1 auto", minWidth: 0, paddingRight: 16 }}>
          <Grid container justifyContent="flex-start" spacing={2} style={{ marginBottom: 10 }}>
            {/* <Grid item xs={4}>
            <TextField
              fullWidth
              size="small"
              label="Mã giải thưởng"
              variant="outlined"
              value={values?.awardCode?.toString() || ""}
              required
              disabled={disabled}
              error={helperText && !values?.awardCode}
              helperText={helperText && !values?.awardCode ? "Vui lòng nhập mã giải thưởng" : ""}
              onChange={(event) => handleChangeForm("awardCode", event.target.value)}
            />
          </Grid> */}
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label="Mã CTKH"
                value={values?.hemisCode || ""}
                disabled={disabled}
                onChange={(event) => handleChangeForm("hemisCode", event.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                size="small"
                // label="Loại giải thưởng"
                variant="outlined"
                value={values?.awardType || ""}
                required
                disabled={disabled}
                error={helperText && !values?.awardType}
                helperText={helperText && !values?.awardType ? "Vui lòng chọn loại giải thưởng" : ""}
                onChange={(event) => handleChangeForm("awardType", event.target.value)}
                SelectProps={{ native: true }}
              >
                <option value="">-- Chọn loại giải thưởng --</option>
                {ACHIEVEMENT.awardType.map((e) => (
                  <option key={e.key} value={e.value}>
                    {e.title}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Cơ quan tổ chức Giải thưởng"
                variant="outlined"
                value={values?.organizingAgency?.toString() || ""}
                disabled={disabled}
                onChange={(event) => handleChangeForm("organizingAgency", event.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Tên đề tài, công trình được trao giải"
                variant="outlined"
                value={values?.awardedProjectName?.toString() || ""}
                required
                disabled={disabled}
                error={helperText && !values?.awardedProjectName}
                helperText={
                  helperText && !values?.awardedProjectName ? "Vui lòng nhập tên đề tài, công trình được trao giải" : ""
                }
                onChange={(event) => handleChangeForm("awardedProjectName", event.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Tên đơn vị được trao giải"
                variant="outlined"
                value={values?.awardedUnitName?.toString() || ""}
                disabled={disabled}
                onChange={(event) => handleChangeForm("awardedUnitName", event.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                size="small"
                // label="Mức giải thưởng"
                variant="outlined"
                value={values?.awardLevel || ""}
                disabled={disabled}
                onChange={(event) => handleChangeForm("awardLevel", event.target.value)}
                SelectProps={{ native: true }}
              >
                <option value="">-- Chọn mức giải thưởng --</option>
                {ACHIEVEMENT.awardLevel.map((e) => (
                  <option key={e.key} value={e.value}>
                    {e.title}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Quyết định cấp bằng khen, giấy khen"
                variant="outlined"
                value={values?.decisionDocument?.toString() || ""}
                disabled={disabled}
                onChange={(event) => handleChangeForm("decisionDocument", event.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Cơ quan/ tổ chức ban hành quyết định"
                variant="outlined"
                value={values?.issuingAgency?.toString() || ""}
                disabled={disabled}
                onChange={(event) => handleChangeForm("issuingAgency", event.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                fullWidth
                size="small"
                disabled={disabled}
                options={(props.departmentList || []).filter((d) => d.id !== 0)}
                getOptionLabel={(option) => option?.name || ""}
                value={
                  (props.departmentList || []).filter((d) => d.id !== 0).find((d) => d.id === values?.departmentId) ||
                  null
                }
                onChange={(event, newValue) => handleChangeForm("departmentId", newValue?.id)}
                renderInput={(params) => <TextField {...params} size="small" label="Đơn vị" variant="outlined" />}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                variant="outlined"
                label="Số lượng tác giả"
                value={values?.numOfAuthors || ""}
                required
                disabled={disabled}
                error={helperText && !values?.numOfAuthors}
                helperText={helperText && !values?.numOfAuthors ? "Vui lòng nhập số lượng tác giả" : ""}
                onChange={(event) => handleChangeForm("numOfAuthors", parseInt(event.target.value))}
              />
            </Grid>
            {/* <Grid item xs={12}>
            <FormControl fullWidth variant="outlined" size="small" disabled={disabled}>
              <InputLabel id="purpose-label" style={{ padding: "0 8px", backgroundColor: "white" }}>
                Mục đích
              </InputLabel>
              <Select
                native
                fullWidth
                labelId="purpose-label"
                value={values?.purposeId ?? 5}
                onChange={(event) => handleChangeForm("purposeId", parseInt(event.target.value))}
              >
                {props.purposes.filter(e => e.name !== 'bonusCalculated').map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.description}
                  </option>
                ))}
              </Select>
            </FormControl>
          </Grid> */}

            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Ghi chú"
                variant="outlined"
                value={values?.note || ""}
                multiline
                rows={3}
                disabled={disabled}
                onChange={(event) => handleChangeForm("note", event.target.value)}
              />
            </Grid>

            <Grid item xs={5} style={{ margin: "auto" }}>
              <Divider />{" "}
            </Grid>
            <Grid item xs={2}>
              <Typography style={{ textAlign: "center", color: "rgba(0, 0, 0, 0.30)" }}>Thành viên</Typography>
            </Grid>
            <Grid item xs={5} style={{ margin: "auto" }}>
              <Divider />{" "}
            </Grid>

            <Grid item xs={12}>
              {profileList.map((e, index) => (
                <Grid key={`list-${index}`} container spacing={2}>
                  <Grid item xs={1}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={e.isInternal !== false}
                          onChange={(event) => handleChangeProfileList(index, "isInternal", event.target.checked)}
                          color="primary"
                          disabled={disabled}
                        />
                      }
                      label="Nội bộ"
                    />
                  </Grid>
                  {e.isInternal && (
                    <Grid item xs={values?.purposeId !== 1 ? 5 : 4}>
                      <Autocomplete
                        disableClearable
                        disabled={disabled}
                        options={props.profileList}
                        getOptionLabel={(option) => option.name}
                        value={profileListFromProps?.find((profile) => profile.id === e.profileId)}
                        onChange={(event, profile) => {
                          if (profileList[index]?.profileId === values?.profileIdForKpi)
                            handleChangeForm("profileIdForKpi", null);
                          handleChangeProfileList(index, "profileId", parseInt(profile?.id));
                        }}
                        renderInput={(params) => (
                          <TextField {...params} size="small" label="Thành viên" variant="outlined" />
                        )}
                      />
                    </Grid>
                  )}
                  {!e.isInternal && (
                    <Grid item xs={values?.purposeId !== 1 ? 5 : 4}>
                      <TextField
                        fullWidth
                        size="small"
                        variant="outlined"
                        label="Tên thành viên"
                        value={e?.externalName || ""}
                        disabled={disabled}
                        onChange={(event) => handleChangeProfileList(index, "externalName", event.target.value)}
                      />
                      {helperText && e.externalName === "" && (
                        <FormHelperText style={{ color: "red" }}>Không được để trống</FormHelperText>
                      )}
                    </Grid>
                  )}
                  <Grid item xs={values?.purposeId !== 1 ? 5 : 3}>
                    <FormControl fullWidth variant="outlined" size="small">
                      <InputLabel id="roleInAchievement-label" style={{ padding: "0 8px", backgroundColor: "white" }}>
                        Vai trò
                      </InputLabel>
                      <Select
                        native
                        fullWidth
                        disabled={disabled}
                        labelId="roleInAchievement-label"
                        value={e.roleInAchievement}
                        onChange={(event) => handleChangeProfileList(index, "roleInAchievement", event.target.value)}
                      >
                        {ACHIEVEMENT.roleOptions.map((role) => (
                          <option key={role.key} value={role.value}>
                            {role.title}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  {values?.purposeId === 1 && (
                    <Grid item xs={3}>
                      <FormControl required fullWidth error={!values?.profileIdForKpi}>
                        <Button
                          fullWidth
                          color="primary"
                          variant="outlined"
                          style={{ height: 40 }}
                          disabled={e.profileId === null || disabled}
                          startIcon={
                            typeof values?.profileIdForKpi === "number" && values?.profileIdForKpi === e.profileId ? (
                              <CheckBox />
                            ) : (
                              <CheckBoxOutlineBlank />
                            )
                          }
                          onClick={() => {
                            setDetail(null);
                            props.getAllEmploymentContractByProfileId(parseInt(e.profileId));
                            handleChangeForm(
                              "profileIdForKpi",
                              values?.profileIdForKpi !== e.profileId ? parseInt(e.profileId) : null,
                            );
                          }}
                        >
                          Tính KPI
                        </Button>
                        {!values?.profileIdForKpi && <FormHelperText>Phải chọn 1 người tính KPI</FormHelperText>}
                      </FormControl>
                    </Grid>
                  )}
                  <Grid item xs={1}>
                    <Button
                      fullWidth
                      color="secondary"
                      variant="contained"
                      style={{ height: 40 }}
                      disabled={disabled}
                      onClick={() => handleRemove(index)}
                    >
                      Xoá
                    </Button>
                  </Grid>
                </Grid>
              ))}
            </Grid>

            {!disabled && (
              <Grid item xs={12}>
                <Button fullWidth variant="outlined" onClick={handleInsert}>
                  Thêm thành viên
                </Button>
              </Grid>
            )}
          </Grid>
          <div
            style={{
              flexDirection: "column",
              display: values?.profileIdForKpi !== null && values?.profileIdForKpi >= 0 ? "block" : "none",
            }}
          >
            <EmploymentContractTable
              contract={detail}
              contractList={contractList}
              kpiIdForKpi={values?.kpiIdForKpi}
              contractIdForKpi={values?.contractIdForKpi}
              setDetail={setDetail}
              values={values}
              setValues={setValues}
              handleChangeForm={handleChangeForm}
            />
            <EmploymentContractDetailTable kpi={detail} />
          </div>
        </div>
        <div style={{ flex: "0 0 380px", paddingLeft: 16, borderLeft: "1px solid #e0e0e0" }}>
          <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
            File minh chứng
          </Typography>
          <MultiFileManager
            key={fileManagerKey}
            fileUrls={fileUrls}
            setFileUrls={setFileUrls}
            newFiles={newFiles}
            setNewFiles={setNewFiles}
            disabled={disabled}
          />
        </div>
      </DialogContent>
      <DialogActions style={{ padding: "0 24px 8px 24px" }}>
        <Grid container justifyContent={"space-between"}>
          <div />
          <div style={{ float: "right" }}>
            {props.adminTab && (
              <Button variant="outlined" onClick={handleSendFeedback} style={{ marginRight: 4 }}>
                Gửi feedback
              </Button>
            )}
            {disabled ? (
              <>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => setUpdating(null)}
                  style={{ marginRight: 4 }}
                >
                  Đóng
                </Button>
                {(props.role === "sudo-admin" || !updating?.locked) && (
                  <Button variant="contained" color="primary" onClick={() => setDisabled(false)}>
                    Chỉnh sửa
                  </Button>
                )}
              </>
            ) : (
              <>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => {
                    setDisabled(true);
                    resetValues();
                  }}
                  style={{ marginRight: 4 }}
                >
                  Huỷ
                </Button>
                <Button variant="contained" color="primary" onClick={handleSubmit}>
                  Lưu
                </Button>
              </>
            )}
          </div>
        </Grid>
      </DialogActions>
      <DuplicateCheckModal
        open={duplicateModalOpen}
        handleClose={handleCloseDuplicateModal}
        duplicateList={duplicateList}
        handleSelect={handleSelectDuplicate}
      />
    </Dialog>
  );
}

const mapStateToProps = (state) => ({
  role: select(state, "userReducer", "role"),
  history: select(state, "homeReducer", "history"),
  profile: select(state, "profileReducer", "detail"),
  purposes: select(state, "scientificResearchReducer", "purposes"),
  profileList: select(state, "profileReducer", "list"),
  profileDepartments: select(state, "profileReducer", "departments"),
  departmentList: select(state, "departmentReducer", "departments"),
  employmentContractListById: select(state, "employmentContractReducer", "listById"),
  employmentContractDetail: select(state, "employmentContractReducer", "detail"),
  isFetchingEmploymentContract: select(state, "employmentContractReducer", "isFetching"),
});

const mapDispatchToProps = (dispatch) => ({
  statisticUnaccepted: (payload) => dispatch(statisticUnaccepted(payload)),
  updateAchievement: (id, payload) => dispatch(updateAchievement(id, payload)),
  uploadMultiFile: (id, files, type) => dispatch(uploadMultiFile(id, files, type)),
  getAllEmploymentContractByProfileId: (id) => dispatch(getAllEmploymentContractByProfileId(id)),
  getEmploymentContractById: (id) => dispatch(getEmploymentContractById(id)),
  checkDuplicateAchievement: (payload) => dispatch(checkDuplicateAchievement(payload)),
  pushNoti: (payload) => dispatch(notification(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(toJs(AchievementUpdateModal));

